import type { TelegramClient } from "telegram";
import type { Broadcast, MediaItem, NextBatchItem, ReportItem } from "@tg/shared";
import { loadTg } from "./tglib";
import { toInputPeer } from "./entityCache";
import { classifyError } from "./errorMatrix";
import { expandSpintax, jitterBytes, varyFileName } from "./spintax";

export interface SendContext {
  client: TelegramClient;
  job: Broadcast;
  mediaBlobs: Blob[]; // one per job.media, in order
}

export interface SendOutcome {
  report?: ReportItem;
  waitMs?: number;
  retrySame?: boolean;
  stop?: "peer_flood" | "auth";
}

const EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
};

function fileNameFor(media: MediaItem, i: number, vary: boolean): string {
  const ext = EXT[media.mime] ?? "jpg";
  const base = `photo${i + 1}.${ext}`;
  return vary ? varyFileName(base) : base;
}

async function buildFile(blob: Blob, media: MediaItem, i: number, job: Broadcast) {
  const { CustomFile } = await loadTg();
  const data = job.varyBytes ? jitterBytes(blob) : blob;
  const bytes = new Uint8Array(await data.arrayBuffer());
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const BufferCtor = (globalThis as any).Buffer;
  const buf = BufferCtor ? BufferCtor.from(bytes) : bytes;
  // CustomFile with an image extension → GramJS uploads it as a PHOTO (not a document).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return new CustomFile(fileNameFor(media, i, job.varyFilename), bytes.length, "", buf as any);
}

function extractIds(result: unknown): string[] {
  if (Array.isArray(result)) {
    return result.map((m) => String((m as { id?: unknown })?.id ?? "")).filter(Boolean);
  }
  const id = (result as { id?: unknown })?.id;
  return id != null ? [String(id)] : [];
}

function captionFor(job: Broadcast): string {
  return job.varyCaption ? expandSpintax(job.captionTemplate) : job.captionTemplate;
}

/** Send a fresh album (1..10 photos) + caption. Captures the created message ids. */
async function sendOne(ctx: SendContext, t: NextBatchItem): Promise<SendOutcome> {
  try {
    const entity = await toInputPeer(ctx.client, t);
    const caption = captionFor(ctx.job);
    const files = await Promise.all(
      ctx.job.media.map((m, i) => buildFile(ctx.mediaBlobs[i]!, m, i, ctx.job)),
    );
    const fileArg = files.length === 1 ? files[0] : files;
    // forceDocument:false → images go as PHOTOS, shown inline (not as attachments).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = await ctx.client.sendFile(entity as any, {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      file: fileArg as any,
      caption,
      forceDocument: false,
      workers: 1,
    });
    return {
      report: { targetId: t.targetId, result: "sent", variant: caption, messageIds: extractIds(result) },
    };
  } catch (err) {
    return mapError(err, t);
  }
}

/** Edit an already-sent message: caption always; photos in-place when the count matches. */
async function editOne(ctx: SendContext, t: NextBatchItem): Promise<SendOutcome> {
  const ids = t.messageIds ?? [];
  if (!ids.length) {
    return { report: { targetId: t.targetId, result: "failed", errorCode: "NO_MESSAGE_IDS" } };
  }
  const caption = captionFor(ctx.job);
  try {
    const entity = await toInputPeer(ctx.client, t);
    const sameCount = ctx.job.media.length === ids.length;
    if (sameCount) {
      for (let i = 0; i < ids.length; i++) {
        const file = await buildFile(ctx.mediaBlobs[i]!, ctx.job.media[i]!, i, ctx.job);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await ctx.client.editMessage(entity as any, {
          message: Number(ids[i]),
          text: i === 0 ? caption : undefined,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          file: file as any,
          forceDocument: false,
        });
      }
      return { report: { targetId: t.targetId, result: "sent", variant: caption } };
    }
    // Album item count changed → Telegram can't add/remove photos; edit the caption only.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await ctx.client.editMessage(entity as any, { message: Number(ids[0]), text: caption });
    return { report: { targetId: t.targetId, result: "failed", errorCode: "EDIT_COUNT_MISMATCH" } };
  } catch (err) {
    const msg = String((err as { errorMessage?: string; message?: string })?.errorMessage ?? (err as Error)?.message ?? "");
    if (/MESSAGE_NOT_MODIFIED/i.test(msg)) {
      return { report: { targetId: t.targetId, result: "sent", variant: caption } };
    }
    if (/MESSAGE_EDIT_TIME_EXPIRED/i.test(msg)) {
      return { report: { targetId: t.targetId, result: "failed", errorCode: "EDIT_TIME_EXPIRED" } };
    }
    return mapError(err, t);
  }
}

function mapError(err: unknown, t: NextBatchItem): SendOutcome {
  const c = classifyError(err);
  switch (c.cls) {
    case "WAIT_GLOBAL":
      return { waitMs: (c.seconds ?? 30) * 1000, retrySame: true };
    case "DEFER_CHAT":
      return {
        report: { targetId: t.targetId, result: "deferred", errorCode: c.code, deferSeconds: c.seconds ?? 60 },
      };
    case "HARD_STOP":
      return { report: { targetId: t.targetId, result: "failed", errorCode: "PEER_FLOOD" }, stop: "peer_flood" };
    case "DEAD":
      return { report: { targetId: t.targetId, result: "skipped", errorCode: c.code } };
    case "FATAL_AUTH":
      return { report: { targetId: t.targetId, result: "failed", errorCode: c.code }, stop: "auth" };
    case "TRANSIENT":
      return { waitMs: 3000, retrySame: true };
    case "MEDIA":
    default:
      return { report: { targetId: t.targetId, result: "failed", errorCode: c.code } };
  }
}

/** Process one target according to the job mode (send a new album or edit a sent one). */
export function processTarget(ctx: SendContext, t: NextBatchItem): Promise<SendOutcome> {
  return ctx.job.mode === "edit" ? editOne(ctx, t) : sendOne(ctx, t);
}
